// Community transports. Member-facing routes are part of the typed RPC surface
// and delegate to the shared endpoint map in @healthos/client-data; the admin
// routes under /api/admin/community are mounted outside buildRpcRouter and stay
// on raw apiFetch here.

import { apiFetch } from "@healthos/api-client";
import {
  createCommunityMoment as sharedCreateCommunityMoment,
  fetchCommunity as sharedFetchCommunity,
  fetchCommunityItemReactors as sharedFetchCommunityItemReactors,
  fetchCommunityMemberProfile as sharedFetchCommunityMemberProfile,
  fetchCommunityMeStats as sharedFetchCommunityMeStats,
  fetchCommunityTeammates as sharedFetchCommunityTeammates,
  fetchWeeklyRecipe as sharedFetchWeeklyRecipe,
  reactToCommunityItem as sharedReactToCommunityItem,
  removeCommunityMoment as sharedRemoveCommunityMoment,
  removeProfileImage as sharedRemoveProfileImage,
  reportCommunityItem as sharedReportCommunityItem,
  seedCommunityDemo as sharedSeedCommunityDemo,
  sendCommunityKudos as sharedSendCommunityKudos,
  shareContent as sharedShareContent,
  updateCommunityPreferences as sharedUpdateCommunityPreferences,
  uploadCommunityAsset as sharedUploadCommunityAsset,
  uploadProfileImage as sharedUploadProfileImage,
} from "@healthos/client-data";
import type { api } from "@healthos/schemas";
import { client, jsonOrThrow } from "./healthos";

export const communityQueryKeys = {
  root: ["community"] as const,
  feed: ["community", "feed"] as const,
  meStats: ["community", "me-stats"] as const,
  teammates: (query: string) => ["community", "teammates", query] as const,
  memberProfile: (userId: string) => ["community", "member-profile", userId] as const,
  weeklyRecipe: () => ["community", "weekly-recipe"] as const,
  adminQueues: ["community", "admin", "queues"] as const,
  adminItems: (queue: api.AdminCommunityQueue) => ["community", "admin", "items", queue] as const,
  adminSettings: (companyId?: string) =>
    ["community", "admin", "settings", companyId ?? null] as const,
  slackWorkspace: ["community", "admin", "slack-workspace"] as const,
  adminWeeklyRecipe: ["community", "admin", "weekly-recipe"] as const,
  adminWalkingChallenge: ["community", "admin", "walking-challenge"] as const,
  adminWeeklyRecipeSchedule: ["community", "admin", "weekly-recipe", "schedule"] as const,
  weeklyRecipeSearch: (query: string) =>
    ["community", "admin", "weekly-recipe", "search", query] as const,
  weeklyRecipeSchedule: (from: string, to: string) =>
    [...communityQueryKeys.adminWeeklyRecipeSchedule, from, to] as const,
  itemReactors: (itemType: api.CommunityReactableItemType, itemId: string) =>
    ["community", "reactors", itemType, itemId] as const,
  recipeList: (offset: number, limit: number, q?: string) =>
    ["community", "admin", "recipes", offset, limit, q ?? ""] as const,
};

export async function fetchCommunity(
  query: api.GetCommunityQuery = {},
  signal?: AbortSignal,
): Promise<api.GetCommunityResponse> {
  return sharedFetchCommunity(client(), query, signal);
}

export async function fetchCommunityMeStats(): Promise<api.GetCommunityMeStatsResponse> {
  return sharedFetchCommunityMeStats(client());
}

export async function fetchWeeklyRecipe(): Promise<api.GetWeeklyCompanyRecipeResponse> {
  return sharedFetchWeeklyRecipe(client());
}

export async function fetchCommunityTeammates(
  query: string,
): Promise<api.ListCommunityTeammatesResponse> {
  return sharedFetchCommunityTeammates(client(), query);
}

export async function fetchCommunityMemberProfile(
  userId: string,
  signal?: AbortSignal,
): Promise<api.GetCommunityMemberProfileResponse> {
  return sharedFetchCommunityMemberProfile(client(), userId, signal);
}

export async function createCommunityMoment(
  input: api.CreateCommunityMomentRequest,
): Promise<api.CreateCommunityMomentResponse> {
  return sharedCreateCommunityMoment(client(), input);
}

export async function removeCommunityMoment(
  id: string,
): Promise<api.RemoveCommunityMomentResponse> {
  return sharedRemoveCommunityMoment(client(), id);
}

export async function uploadCommunityAsset(
  input: api.UploadCommunityAssetRequest,
): Promise<api.UploadCommunityAssetResponse> {
  return sharedUploadCommunityAsset(client(), input);
}

export async function uploadProfileImage(
  input: api.UploadProfileImageRequest,
): Promise<api.UploadProfileImageResponse> {
  return sharedUploadProfileImage(client(), input);
}

export async function removeProfileImage(): Promise<api.RemoveProfileImageResponse> {
  return sharedRemoveProfileImage(client());
}

export async function fetchCommunityAssetBlob(url: string, signal?: AbortSignal): Promise<Blob> {
  const res = await apiFetch(url, { signal });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`GET ${url} failed: ${res.status}${body ? ` ${body}` : ""}`);
  }
  return res.blob();
}

export async function sendCommunityKudos(
  input: api.SendCommunityKudosRequest,
): Promise<api.SendCommunityKudosResponse> {
  return sharedSendCommunityKudos(client(), input);
}

export async function shareContent(
  input: api.ShareContentRequest,
): Promise<api.ShareContentResponse> {
  return sharedShareContent(client(), input);
}

export async function reactToCommunityItem(
  input: api.ReactToCommunityItemRequest,
): Promise<api.ReactToCommunityItemResponse> {
  return sharedReactToCommunityItem(client(), input);
}

export async function fetchCommunityItemReactors(
  query: api.ListCommunityItemReactionsQuery,
): Promise<api.ListCommunityItemReactionsResponse> {
  return sharedFetchCommunityItemReactors(client(), query);
}

export async function reportCommunityItem(
  input: api.ReportCommunityItemRequest,
): Promise<api.ReportCommunityItemResponse> {
  return sharedReportCommunityItem(client(), input);
}

export async function updateCommunityPreferences(
  input: api.UpdateCommunityPreferencesRequest,
): Promise<api.UpdateCommunityPreferencesResponse> {
  return sharedUpdateCommunityPreferences(client(), input);
}

export async function seedCommunityDemo(): Promise<api.CommunityDevSeedResponse> {
  return sharedSeedCommunityDemo(client());
}

export async function fetchAdminQueues(): Promise<api.AdminCommunityQueuesResponse> {
  return jsonOrThrow<api.AdminCommunityQueuesResponse>(
    await apiFetch("/api/admin/community/queues"),
    "GET /api/admin/community/queues",
  );
}

export async function fetchAdminItems(
  queue: api.AdminCommunityQueue,
): Promise<api.AdminCommunityItemsResponse> {
  const params = new URLSearchParams({ queue, limit: "60" });
  return jsonOrThrow<api.AdminCommunityItemsResponse>(
    await apiFetch(`/api/admin/community/items?${params.toString()}`),
    "GET /api/admin/community/items",
  );
}

export async function adminCommunityAction(
  itemId: string,
  action: "approve" | "reject" | "remove" | "restore",
  input: api.AdminCommunityActionRequest = {},
): Promise<api.AdminCommunityActionResponse> {
  return jsonOrThrow<api.AdminCommunityActionResponse>(
    await apiFetch(`/api/admin/community/items/${itemId}/${action}`, {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
    }),
    `POST /api/admin/community/items/${action}`,
  );
}

export async function resolveAdminItemReports(
  itemId: string,
): Promise<api.AdminCommunityActionResponse> {
  return jsonOrThrow<api.AdminCommunityActionResponse>(
    await apiFetch(`/api/admin/community/items/${itemId}/resolve-report`, { method: "POST" }),
    "POST /api/admin/community/items/resolve-report",
  );
}

// Slack workspace installation (OAuth multi-tenant, ADR-0028). Admin-only.

export async function fetchSlackWorkspace(): Promise<api.SlackWorkspaceStatusResponse> {
  return jsonOrThrow<api.SlackWorkspaceStatusResponse>(
    await client().api.slack.workspace.$get(),
    "GET /api/slack/workspace",
  );
}

export async function fetchSlackInstallUrl(): Promise<api.SlackInstallUrlResponse> {
  return jsonOrThrow<api.SlackInstallUrlResponse>(
    await client().api.slack.workspace["install-url"].$post(),
    "POST /api/slack/workspace/install-url",
  );
}

export async function disconnectSlackWorkspace(): Promise<api.SlackWorkspaceDisconnectResponse> {
  return jsonOrThrow<api.SlackWorkspaceDisconnectResponse>(
    await client().api.slack.workspace.$delete(),
    "DELETE /api/slack/workspace",
  );
}

/** `companyId` is only ever honored server-side for a platform caller
 *  (ops:all_companies) managing channel routing on behalf of a company
 *  selected in the ops switcher, not their own - see platformCompanyOverride
 *  in apps/core-api/src/routes/community.ts. */
export async function fetchAdminSettings(companyId?: string): Promise<api.CommunitySettings> {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  return jsonOrThrow<api.CommunitySettings>(
    await apiFetch(`/api/admin/community/settings${query}`),
    "GET /api/admin/community/settings",
  );
}

export async function updateAdminSettings(
  input: api.UpdateCommunitySettingsRequest,
  companyId?: string,
): Promise<api.CommunitySettings> {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  return jsonOrThrow<api.CommunitySettings>(
    await apiFetch(`/api/admin/community/settings${query}`, {
      method: "PUT",
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
    }),
    "PUT /api/admin/community/settings",
  );
}

export async function fetchAdminWalkingChallenge(
  companyId?: string,
): Promise<api.GetAdminCompanyWalkingChallengeResponse> {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  return jsonOrThrow<api.GetAdminCompanyWalkingChallengeResponse>(
    await apiFetch(`/api/admin/community/walking-challenge${query}`),
    "GET /api/admin/community/walking-challenge",
  );
}

export async function updateAdminWalkingChallenge(
  input: api.UpdateCompanyWalkingChallengeRequest,
  companyId?: string,
): Promise<api.GetAdminCompanyWalkingChallengeResponse> {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  return jsonOrThrow<api.GetAdminCompanyWalkingChallengeResponse>(
    await apiFetch(`/api/admin/community/walking-challenge${query}`, {
      method: "PUT",
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
    }),
    "PUT /api/admin/community/walking-challenge",
  );
}

export async function createAdminWalkingChallenge(
  input: api.CreateCompanyWalkingChallengeRequest,
  companyId?: string,
): Promise<api.GetAdminCompanyWalkingChallengeResponse> {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  return jsonOrThrow<api.GetAdminCompanyWalkingChallengeResponse>(
    await apiFetch(`/api/admin/community/walking-challenge${query}`, {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
    }),
    "POST /api/admin/community/walking-challenge",
  );
}

export async function createScheduledAdminWalkingChallenge(
  input: api.CreateScheduledCompanyWalkingChallengeRequest,
  companyId?: string,
): Promise<api.GetAdminCompanyWalkingChallengeResponse> {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  return jsonOrThrow<api.GetAdminCompanyWalkingChallengeResponse>(
    await apiFetch(`/api/admin/community/walking-challenge/scheduled${query}`, {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
    }),
    "POST /api/admin/community/walking-challenge/scheduled",
  );
}

export async function updateScheduledAdminWalkingChallenge(
  input: api.UpdateScheduledCompanyWalkingChallengeRequest,
  companyId?: string,
): Promise<api.GetAdminCompanyWalkingChallengeResponse> {
  const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  return jsonOrThrow<api.GetAdminCompanyWalkingChallengeResponse>(
    await apiFetch(`/api/admin/community/walking-challenge/scheduled${query}`, {
      method: "PUT",
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
    }),
    "PUT /api/admin/community/walking-challenge/scheduled",
  );
}

export async function fetchAdminWeeklyRecipe(): Promise<{
  recipe: api.WeeklyCompanyRecipe | null;
}> {
  return jsonOrThrow<{ recipe: api.WeeklyCompanyRecipe | null }>(
    await apiFetch("/api/admin/community/weekly-recipe"),
    "GET /api/admin/community/weekly-recipe",
  );
}

export async function fetchWeeklyRecipeSchedule(
  from: string,
  to: string,
): Promise<api.WeeklyRecipeScheduleResponse> {
  const params = new URLSearchParams({ from, to });
  return jsonOrThrow<api.WeeklyRecipeScheduleResponse>(
    await apiFetch(`/api/admin/community/weekly-recipe/schedule?${params.toString()}`),
    "GET /api/admin/community/weekly-recipe/schedule",
  );
}

export async function fetchRecipeList(
  offset: number,
  limit: number,
  q?: string,
): Promise<api.RecipeListResponse> {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  if (q) params.set("q", q);
  return jsonOrThrow<api.RecipeListResponse>(
    await apiFetch(`/api/admin/community/recipes?${params.toString()}`),
    "GET /api/admin/community/recipes",
  );
}

export async function selectWeeklyRecipe(
  cmsUuid: string,
  adminMessage?: string | null,
  weekStart?: string,
): Promise<{ recipe: api.WeeklyCompanyRecipe }> {
  // `adminMessage` is the admin-authored note that surfaces in the resource
  // dialog's "Why this, for you" callout. The contract accepts an optional
  // string (max 500). We send the trimmed message when the admin set one; an
  // explicit `null` (admin cleared the textarea) is forwarded as an empty
  // string so the server stores null/empty and the dialog falls back to the
  // recipe summary.
  const body: api.SelectWeeklyRecipeRequest = { cmsUuid };
  if (typeof adminMessage === "string" && adminMessage.trim().length > 0) {
    body.adminMessage = adminMessage.trim();
  } else if (adminMessage === null) {
    body.adminMessage = "";
  }
  if (weekStart) body.weekStart = weekStart;
  return jsonOrThrow<{ recipe: api.WeeklyCompanyRecipe }>(
    await apiFetch("/api/admin/community/weekly-recipe", {
      method: "PUT",
      body: JSON.stringify(body),
      headers: { "content-type": "application/json" },
    }),
    "PUT /api/admin/community/weekly-recipe",
  );
}

export async function autoSelectWeeklyRecipe(): Promise<{
  recipe: api.WeeklyCompanyRecipe;
}> {
  return jsonOrThrow<{ recipe: api.WeeklyCompanyRecipe }>(
    await apiFetch("/api/admin/community/weekly-recipe/auto-select", {
      method: "POST",
    }),
    "POST /api/admin/community/weekly-recipe/auto-select",
  );
}

// Recipe search for the admin panel, via the typed content search route.
export async function searchRecipes(query: string): Promise<api.ContentSearchResponse> {
  const trimmed = query.trim();
  return jsonOrThrow<api.ContentSearchResponse>(
    await client().api.content.search.$get({
      query: {
        contextText: trimmed,
        contentType: "Recipe",
        limit: "12",
      },
    }),
    "GET /api/content/search",
  );
}
